import { useState } from 'react';
import { Modal, Button, Spinner, Badge, Accordion, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaChevronDown, FaChevronRight, FaExternalLinkAlt } from 'react-icons/fa';
import moment from 'moment';
import PropTypes from 'prop-types';
import styles from './ModalDetalhesDuplicidade.module.css';

const MAP_ATENDIMENTO_TIPO = {
    programa: "Atendimento do Programa",
    midias: "Mídias",
    duvida_tecnica: "Dúvida Técnica",
    duvida: "Dúvida Técnica",
};

const MAP_STATUS_BADGE = {
    aprovado: "success",
    realizado: "success",
    pendente: "warning",
    agendado: "primary",
    cancelado: "danger",
    reprovado: "danger",
    finalizado: "success",
};

const MAP_STATUS_LABEL = {
    aprovado: "Aprovado",
    realizado: "Realizado",
    pendente: "Pendente",
    agendado: "Agendado",
    cancelado: "Cancelado",
    reprovado: "Reprovado",
    finalizado: "Finalizado",
};

const MAP_TIPO_SERVICO_BADGE = {
    consulta: "primary",
    exame: "info",
    procedimento: "secondary",
};

/**
 * Componente de linha expansível para tabelas com accordion
 */
function ExpandableRow({ id, children, details }) {
    const [expanded, setExpanded] = useState(false);

    return (
        <>
            <tr 
                onClick={() => setExpanded(!expanded)} 
                style={{ cursor: 'pointer' }}
                className={`align-middle ${styles.expandableRow}`}
            >
                <td className="ps-3" style={{ width: '40px' }}>
                    {expanded ? <FaChevronDown /> : <FaChevronRight />}
                </td>
                {children}
            </tr>
            {expanded && (
                <tr>
                    <td colSpan="100%" className="p-0">
                        <div className={`bg-light p-3 border-top ${styles.expandedContent}`}>
                            {details}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

ExpandableRow.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    children: PropTypes.node.isRequired,
    details: PropTypes.node.isRequired,
};

export default function ModalDetalhesDuplicidade({ show, onHide, loading, data, itemName }) {
    
    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        return moment(dateStr).format('DD/MM/YYYY HH:mm');
    };

    const formatCurrency = (value) => {
        if (!value) return "-";
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    const getStatusBadge = (status) => {
        if (!status) return <span className="text-muted">-</span>;
        const variant = MAP_STATUS_BADGE[status.toLowerCase()] || "secondary";
        const label = MAP_STATUS_LABEL[status.toLowerCase()] || status;
        return <Badge bg={variant}>{label}</Badge>;
    };

    const getTipoServicoBadge = (tipo) => {
        if (!tipo) return <span className="text-muted">-</span>;
        const variant = MAP_TIPO_SERVICO_BADGE[tipo.toLowerCase()] || "secondary";
        return <Badge bg={variant}>{tipo}</Badge>;
    };

    const getTipoLabel = (tipo) => {
        return MAP_ATENDIMENTO_TIPO[tipo] || tipo;
    };

    const renderLink = (id, text, type = 'beneficiario') => {
        if (!id) return <span className="text-muted">-</span>;
        return (
            <span>
                {text || `#${id}`}
            </span>
        );
    };

    // Renderizar Atendimentos
    const renderAtendimentos = () => {
        if (!data?.atendimentos || data.atendimentos.length === 0) return null;

        return (
            <div className="card mb-4 border shadow-sm">
                <div className="card-header bg-white py-3">
                    <h5 className="text-primary m-0 font-weight-bold">
                        Atendimentos ({data.atendimentos.length})
                    </h5>
                </div>
                <div className="table-responsive m-0">
                    <table className="table table-hover mb-0">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '40px' }}></th>
                                <th style={{ width: '80px' }}>ID</th>
                                <th>Beneficiário</th>
                                <th>Etapa</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.atendimentos.map((at) => (
                                <ExpandableRow
                                    key={at.id}
                                    id={at.id}
                                    details={
                                        <div className="row">
                                            <div className="col-md-6">
                                                <p className="mb-2">
                                                    <strong>Tipo:</strong> {getTipoLabel(at.tipo_atendimento)}
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Data:</strong> {formatDate(at.data)}
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Beneficiário ID:</strong> {at.beneficiario_id || '-'}
                                                </p>
                                            </div>
                                            <div className="col-md-6">
                                                <p className="mb-2">
                                                    <strong>Etapa ID:</strong> {at.etapa_id || '-'}
                                                </p>
                                                {(at.atendimento_uuid || at.uuid) ? (
                                                    <div className="mt-3">
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            onClick={() => window.open(`/atendimentos/edit/${at.atendimento_uuid || at.uuid}`, '_blank')}
                                                        >
                                                            Ver Atendimento
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <p className="text-muted small mt-3">
                                                        UUID não encontrado para exibir o botão de link do atendimento
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    }
                                >
                                    <td>{at.id}</td>
                                    <td>{renderLink(at.beneficiario_id, at.beneficiario_nome, 'beneficiário')}</td>
                                    <td>{at.etapa_nome || '-'}</td>
                                    <td>{getStatusBadge(at.status_aprovacao)}</td>
                                </ExpandableRow>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // Renderizar Agendamentos
    const renderAgendamentos = () => {
        if (!data?.agendamentos || data.agendamentos.length === 0) return null;

        return (
            <div className="card mb-4 border shadow-sm">
                <div className="card-header bg-white py-3">
                    <h5 className="text-primary m-0 font-weight-bold">
                        Agendamentos ({data.agendamentos.length})
                    </h5>
                </div>
                <div className="table-responsive m-0">
                    <table className="table table-hover mb-0">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '40px' }}></th>
                                <th style={{ width: '80px' }}>ID</th>
                                <th>Beneficiário</th>
                                <th>Serviço/Procedimento</th>
                                <th>Situação</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.agendamentos.map((ag) => (
                                <ExpandableRow
                                    key={ag.id}
                                    id={ag.id}
                                    details={
                                        <div className="row">
                                            <div className="col-md-6">
                                                <p className="mb-2">
                                                    <strong>Tipo Serviço:</strong> {getTipoServicoBadge(ag.servico_procedimento_tipo)}
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Etapa:</strong> {ag.etapa_nome || '-'}
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Data Agendamento:</strong> {formatDate(ag.data_agendamento)}
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Data Liberação:</strong> {formatDate(ag.data_liberacao)}
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Data Realização:</strong> {formatDate(ag.data_realizacao)}
                                                </p>
                                            </div>
                                            <div className="col-md-6">
                                                <p className="mb-2">
                                                    <strong>Valor:</strong> {formatCurrency(ag.valor)}
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Atendimento ID:</strong> {ag.atendimento_id || '-'}
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Etapa ID:</strong> {ag.etapa_id || '-'}
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Finalizado Por:</strong> {ag.finalizado_por_nome || '-'}
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Serviço/Procedimento ID:</strong> {ag.servico_procedimento_id || '-'}
                                                </p>
                                                {(ag.atendimento_uuid || ag.uuid) ? (
                                                    <div className="mt-3">
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            onClick={() => window.open(`/atendimentos/edit/${ag.atendimento_uuid || ag.uuid}`, '_blank')}
                                                        >
                                                            Ver Atendimento
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <p className="text-muted small mt-3">
                                                        UUID não encontrado para exibir o botão de link do atendimento
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    }
                                >
                                    <td>{ag.id}</td>
                                    <td>{renderLink(ag.beneficiario_id, ag.beneficiario_nome, 'beneficiário')}</td>
                                    <td>{renderLink(ag.servico_procedimento_id, ag.servico_procedimento_nome, 'serviço')}</td>
                                    <td>{getStatusBadge(ag.situacao)}</td>
                                </ExpandableRow>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // Renderizar Exames (para médicos)
    const renderExamesMedicos = () => {
        if (!data?.exames || data.exames.length === 0) return null;
        
        // Verificar se são exames de médicos (não têm beneficiario_nome)
        const isMedicoExames = !data.exames[0]?.beneficiario_nome;
        if (!isMedicoExames) return null;

        return (
            <div className="card mb-4 border shadow-sm">
                <div className="card-header bg-white py-3">
                    <h5 className="text-primary m-0 font-weight-bold">
                        Exames ({data.exames.length})
                    </h5>
                </div>
                <div className="table-responsive m-0">
                    <table className="table table-hover mb-0">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '40px' }}></th>
                                <th style={{ width: '80px' }}>ID</th>
                                <th>Status</th>
                                <th>Data Agendamento</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.exames.map((ex) => (
                                <ExpandableRow
                                    key={ex.id}
                                    id={ex.id}
                                    details={
                                        <div className="row">
                                            <div className="col-md-6">
                                                <p className="mb-2">
                                                    <strong>Data Realização:</strong> {formatDate(ex.data_realizacao)}
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Data Recebimento:</strong> {formatDate(ex.data_recebimento)}
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Motivo:</strong> {ex.motivo || '-'}
                                                </p>
                                            </div>
                                            <div className="col-md-6">
                                                {ex.motivo_outros && (
                                                    <p className="mb-2">
                                                        <strong>Motivo (Outros):</strong>
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip>{ex.motivo_outros}</Tooltip>}
                                                        >
                                                            <span className="text-primary ms-2" style={{ cursor: 'help' }}>
                                                                Ver detalhes
                                                            </span>
                                                        </OverlayTrigger>
                                                    </p>
                                                )}
                                                {(ex.atendimento_uuid || ex.uuid) ? (
                                                    <div className="mt-3">
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            onClick={() => window.open(`/atendimentos/edit/${ex.atendimento_uuid || ex.uuid}`, '_blank')}
                                                        >
                                                            Ver Atendimento
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <p className="text-muted small mt-3">
                                                        UUID não encontrado para exibir o botão de link do atendimento
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    }
                                >
                                    <td>{ex.id}</td>
                                    <td>{getStatusBadge(ex.status)}</td>
                                    <td>{formatDate(ex.data_agendamento)}</td>
                                </ExpandableRow>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // Renderizar Exames Solicitados (para laboratórios/provedores)
    const renderExamesSolicitados = () => {
        if (!data?.exames || data.exames.length === 0) return null;
        
        // Verificar se são exames de laboratórios (têm beneficiario_nome)
        const isLabExames = !!data.exames[0]?.beneficiario_nome;
        if (!isLabExames) return null;

        return (
            <div className="card mb-4 border shadow-sm">
                <div className="card-header bg-white py-3">
                    <h5 className="text-primary m-0 font-weight-bold">
                        Exames Solicitados ({data.exames.length})
                    </h5>
                </div>
                <div className="table-responsive m-0">
                    <table className="table table-hover mb-0">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '40px' }}></th>
                                <th style={{ width: '80px' }}>ID</th>
                                <th>Beneficiário</th>
                                <th>Serviço/Procedimento</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.exames.map((ex) => (
                                <ExpandableRow
                                    key={ex.id}
                                    id={ex.id}
                                    details={
                                        <div className="row">
                                            <div className="col-md-6">
                                                <p className="mb-2">
                                                    <strong>Tipo Serviço:</strong> {getTipoServicoBadge(ex.servico_procedimento_tipo)}
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Atendimento ID:</strong> {ex.atendimento_id || '-'}
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Etapa:</strong> {ex.etapa_nome || '-'}
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Data Agendamento:</strong> {formatDate(ex.data_agendamento)}
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Data Realização:</strong> {formatDate(ex.data_realizacao)}
                                                </p>
                                            </div>
                                            <div className="col-md-6">
                                                <p className="mb-2">
                                                    <strong>Data Aprovação:</strong> {formatDate(ex.data_aprovacao)}
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Etapa ID:</strong> {ex.etapa_id || '-'}
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Serviço/Procedimento ID:</strong> {ex.servico_procedimento_id || '-'}
                                                </p>
                                                {ex.motivo_status && (
                                                    <p className="mb-2">
                                                        <strong>Motivo:</strong>
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip>{ex.motivo_status}</Tooltip>}
                                                        >
                                                            <span className="text-primary ms-2" style={{ cursor: 'help' }}>
                                                                Ver detalhes
                                                            </span>
                                                        </OverlayTrigger>
                                                    </p>
                                                )}
                                                {(ex.atendimento_uuid || ex.uuid) ? (
                                                    <div className="mt-3">
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            onClick={() => window.open(`/atendimentos/edit/${ex.atendimento_uuid || ex.uuid}`, '_blank')}
                                                        >
                                                            Ver Atendimento
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <p className="text-muted small mt-3">
                                                        UUID não encontrado para exibir o botão de link do atendimento
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    }
                                >
                                    <td>{ex.id}</td>
                                    <td>{renderLink(ex.beneficiario_id, ex.beneficiario_nome, 'beneficiário')}</td>
                                    <td>{renderLink(ex.servico_procedimento_id, ex.servico_procedimento_nome, 'serviço')}</td>
                                    <td>{getStatusBadge(ex.status)}</td>
                                </ExpandableRow>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>Detalhes: {itemName}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                        <p className="mt-2">Carregando detalhes...</p>
                    </div>
                ) : (
                    data && (
                        <div>
                            {renderAtendimentos()}
                            {renderAgendamentos()}
                            {renderExamesMedicos()}
                            {renderExamesSolicitados()}
                            
                            {(!data.atendimentos?.length && !data.agendamentos?.length && !data.exames?.length) && (
                                <p className="text-center text-muted">
                                    Nenhum detalhe encontrado (Atendimentos, Agendamentos ou Exames).
                                </p>
                            )}
                        </div>
                    )
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Fechar</Button>
            </Modal.Footer>
        </Modal>
    );
}

ModalDetalhesDuplicidade.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    data: PropTypes.shape({
        atendimentos: PropTypes.array,
        agendamentos: PropTypes.array,
        exames: PropTypes.array,
    }),
    itemName: PropTypes.string,
};
